import { useState, useEffect, useMemo } from 'react';
import Layout from '../components/Layout';
import { api } from '../lib/api';

interface Character {
  characterId: string;
  characterName: string;
  corporationId: string | null;
  corporationName: string | null;
  allianceId: string | null;
  allianceName: string | null;
  syncStatus: 'never' | 'success' | 'failed' | 'in_progress';
  lastSyncAt: string | null;
  orderCount: number | null;
  errorMessage: string | null;
}

interface Corporation {
  corporationId: string;
  corporationName: string;
  syncStatus: 'never' | 'success' | 'failed' | 'in_progress';
  lastSyncAt: string | null;
  orderCount: number | null;
  errorMessage: string | null;
}

interface BuyOrder {
  id: string;
  orderId: string;
  characterId: string;
  characterName: string;
  typeId: number;
  typeName: string;
  locationId: string;
  regionId: number;
  price: number;
  escrow: number;
  isCorporation: boolean;
  issued: string;
  expiryDate: string;
  duration: number;
  minVolume: number;
  range: string;
  rangeName: string;
  volumeRemain: number;
  volumeTotal: number;
  volumeFilled: number;
  fillPercentage: number;
}

interface OrderSummary {
  total: number;
  totalEscrow: number;
  byCharacter: Array<{
    characterId: string;
    characterName: string;
    orderCount: number;
    totalEscrow: number;
  }>;
}

interface TreeNode {
  id: string;
  type: 'summary' | 'character' | 'corporation';
  character?: Character;
  corporation?: Corporation;
  expanded: boolean;
}

function formatTimeAgo(dateString: string | null): string {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

function formatTimeRemaining(expiryDate: string): string {
  const end = new Date(expiryDate);
  const now = new Date();
  const diffMs = end.getTime() - now.getTime();

  if (diffMs <= 0) return 'Expired';

  const diffDays = Math.floor(diffMs / 86400000);
  const diffHours = Math.floor((diffMs % 86400000) / 3600000);

  if (diffDays > 0) return `${diffDays}d ${diffHours}h`;
  return `${diffHours}h`;
}

function formatISK(value: number): string {
  if (value >= 1_000_000_000) {
    return (value / 1_000_000_000).toFixed(2) + 'B';
  }
  if (value >= 1_000_000) {
    return (value / 1_000_000).toFixed(2) + 'M';
  }
  if (value >= 1_000) {
    return (value / 1_000).toFixed(2) + 'K';
  }
  return value.toFixed(2);
}

function BuyOrders() {
  const [characters, setCharacters] = useState<Character[]>([]);
  const [corporations, setCorporations] = useState<Corporation[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedNode, setSelectedNode] = useState<TreeNode | null>(null);
  const [orders, setOrders] = useState<BuyOrder[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [summary, setSummary] = useState<OrderSummary | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set(['summary', 'characters', 'corporations']));
  const [syncing, setSyncing] = useState<string | null>(null);
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState(0);
  const [refreshTotal, setRefreshTotal] = useState(0);
  const [rangeFilter, setRangeFilter] = useState<string>('all');
  const [error, setError] = useState<string | null>(null);

  // Fetch characters and corporations on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [charsResponse, corpsResponse] = await Promise.all([
          api.get('/buy-orders/characters'),
          api.get('/buy-orders/corporations'),
        ]);
        setCharacters(charsResponse.data.characters);
        setCorporations(corpsResponse.data.corporations || []);
        // Select summary by default
        setSelectedNode({ id: 'summary', type: 'summary', expanded: true });
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Fetch orders when node is selected
  useEffect(() => {
    const fetchOrders = async () => {
      setOrdersLoading(true);
      setError(null);
      try {
        let ordersPromise;
        if (selectedNode?.type === 'character' && selectedNode.character) {
          ordersPromise = api.get('/buy-orders/orders', { params: { characterId: selectedNode.character.characterId } });
        } else if (selectedNode?.type === 'corporation' && selectedNode.corporation) {
          ordersPromise = api.get(`/buy-orders/corporation/${selectedNode.corporation.corporationId}/orders`);
        } else {
          // Summary view - fetch all character orders
          ordersPromise = api.get('/buy-orders/orders');
        }

        const [ordersResponse, summaryResponse] = await Promise.all([
          ordersPromise,
          api.get('/buy-orders/summary'),
        ]);

        setOrders(ordersResponse.data.orders);
        setSummary(summaryResponse.data);
      } catch (error: any) {
        console.error('Failed to fetch orders:', error);
        setError(error.response?.data?.error || 'Failed to fetch buy orders');
      } finally {
        setOrdersLoading(false);
      }
    };

    if (selectedNode) {
      fetchOrders();
    }
  }, [selectedNode]);

  // Sync orders for a character
  const handleSync = async (characterId: string) => {
    if (syncing) return;

    setSyncing(characterId);
    try {
      await api.post(`/buy-orders/character/${characterId}/sync`);
      // Refresh characters and orders
      const [charsResponse, ordersResponse, summaryResponse] = await Promise.all([
        api.get('/buy-orders/characters'),
        api.get('/buy-orders/orders'),
        api.get('/buy-orders/summary'),
      ]);
      setCharacters(charsResponse.data.characters);
      setOrders(ordersResponse.data.orders);
      setSummary(summaryResponse.data);
    } catch (error: any) {
      console.error('Failed to sync orders:', error);
      setError(error.response?.data?.message || error.response?.data?.error || 'Failed to sync buy orders');
    } finally {
      setSyncing(null);
    }
  };

  // Refresh all characters
  const handleRefreshAll = async () => {
    if (refreshingAll || characters.length === 0) return;

    setRefreshingAll(true);
    setRefreshProgress(0);
    setRefreshTotal(characters.length);

    for (let i = 0; i < characters.length; i++) {
      const character = characters[i];
      setRefreshProgress(i + 1);

      try {
        await api.post(`/buy-orders/character/${character.characterId}/sync`);
      } catch (error) {
        console.error(`Failed to sync orders for ${character.characterName}:`, error);
      }
    }

    // Refresh all data
    try {
      const [charsResponse, ordersResponse, summaryResponse] = await Promise.all([
        api.get('/buy-orders/characters'),
        api.get('/buy-orders/orders'),
        api.get('/buy-orders/summary'),
      ]);
      setCharacters(charsResponse.data.characters);
      setOrders(ordersResponse.data.orders);
      setSummary(summaryResponse.data);
    } catch (error) {
      console.error('Failed to refresh data:', error);
    }

    setRefreshingAll(false);
  };

  // Filter orders
  const filteredOrders = useMemo(() => {
    let filtered = orders;

    if (rangeFilter !== 'all') {
      filtered = filtered.filter(o => o.range === rangeFilter);
    }

    // Character/Corporation filtering is already handled by the API call

    return filtered;
  }, [orders, rangeFilter]);

  // Get unique ranges for filter
  const uniqueRanges = useMemo(() => {
    return [...new Set(orders.map(o => o.range))].sort();
  }, [orders]);

  // Toggle node expansion
  const toggleNode = (nodeId: string) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="h-full flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <svg className="animate-spin h-10 w-10 text-blue-500" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="text-gray-400">Loading buy orders...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="h-full flex">
        {/* Left Panel - Tree */}
        <div className="w-72 border-r border-gray-700 flex flex-col bg-gray-900">
          {/* Header */}
          <div className="p-4 border-b border-gray-700">
            <h2 className="text-lg font-semibold text-white">Buy Orders</h2>
            <p className="text-sm text-gray-400 mt-1">
              {characters.length} character{characters.length !== 1 ? 's' : ''}
            </p>
          </div>

          {/* Tree */}
          <div className="flex-1 overflow-y-auto p-2">
            {/* Summary Node */}
            <div
              className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                selectedNode?.type === 'summary' ? 'bg-blue-600' : 'hover:bg-gray-800'
              }`}
              onClick={() => setSelectedNode({ id: 'summary', type: 'summary', expanded: true })}
            >
              <svg className="w-5 h-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <span className={`font-medium ${selectedNode?.type === 'summary' ? 'text-white' : 'text-gray-300'}`}>
                Summary
              </span>
              {summary && (
                <span className="ml-auto text-xs text-gray-400">
                  {summary.total} orders
                </span>
              )}
            </div>

            {/* Characters Section */}
            <div className="mt-2">
              <div
                className="flex items-center gap-2 px-3 py-2 text-gray-400 cursor-pointer hover:text-gray-300"
                onClick={() => toggleNode('characters')}
              >
                <svg
                  className={`w-4 h-4 transition-transform ${expandedNodes.has('characters') ? 'rotate-90' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                <span className="text-sm font-medium uppercase tracking-wider">Characters</span>
              </div>

              {expandedNodes.has('characters') && (
                <div className="ml-4 space-y-1">
                  {characters.map(character => (
                    <div
                      key={character.characterId}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                        selectedNode?.type === 'character' && selectedNode.character?.characterId === character.characterId
                          ? 'bg-blue-600'
                          : 'hover:bg-gray-800'
                      }`}
                      onClick={() => setSelectedNode({ id: character.characterId, type: 'character', character, expanded: true })}
                    >
                      <img
                        src={`https://images.evetech.net/characters/${character.characterId}/portrait?size=32`}
                        alt={character.characterName}
                        className="w-6 h-6 rounded"
                      />
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm truncate ${
                          selectedNode?.type === 'character' && selectedNode.character?.characterId === character.characterId
                            ? 'text-white font-medium'
                            : 'text-gray-300'
                        }`}>
                          {character.characterName}
                        </p>
                        <p className="text-xs text-gray-500">
                          {character.orderCount !== null ? `${character.orderCount} orders` : 'Not synced'}
                        </p>
                      </div>
                      {/* Sync status indicator */}
                      <div className={`w-2 h-2 rounded-full ${
                        character.syncStatus === 'success' ? 'bg-green-500' :
                        character.syncStatus === 'failed' ? 'bg-red-500' :
                        character.syncStatus === 'in_progress' ? 'bg-yellow-500 animate-pulse' :
                        'bg-gray-600'
                      }`} title={`Sync: ${character.syncStatus}`} />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Corporations Section */}
            {corporations.length > 0 && (
              <div className="mt-2">
                <div
                  className="flex items-center gap-2 px-3 py-2 text-gray-400 cursor-pointer hover:text-gray-300"
                  onClick={() => toggleNode('corporations')}
                >
                  <svg
                    className={`w-4 h-4 transition-transform ${expandedNodes.has('corporations') ? 'rotate-90' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <span className="text-sm font-medium uppercase tracking-wider">Corporations</span>
                </div>

                {expandedNodes.has('corporations') && (
                  <div className="ml-4 space-y-1">
                    {corporations.map(corporation => (
                      <div
                        key={corporation.corporationId}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                          selectedNode?.type === 'corporation' && selectedNode.corporation?.corporationId === corporation.corporationId
                            ? 'bg-blue-600'
                            : 'hover:bg-gray-800'
                        }`}
                        onClick={() => setSelectedNode({ id: corporation.corporationId, type: 'corporation', corporation, expanded: true })}
                      >
                        <img
                          src={`https://images.evetech.net/corporations/${corporation.corporationId}/logo?size=32`}
                          alt={corporation.corporationName}
                          className="w-6 h-6 rounded"
                        />
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm truncate ${
                            selectedNode?.type === 'corporation' && selectedNode.corporation?.corporationId === corporation.corporationId
                              ? 'text-white font-medium'
                              : 'text-gray-300'
                          }`}>
                            {corporation.corporationName}
                          </p>
                          <p className="text-xs text-gray-500">
                            {corporation.orderCount !== null ? `${corporation.orderCount} orders` : 'Not synced'}
                          </p>
                        </div>
                        {/* Sync status indicator */}
                        <div className={`w-2 h-2 rounded-full ${
                          corporation.syncStatus === 'success' ? 'bg-green-500' :
                          corporation.syncStatus === 'failed' ? 'bg-red-500' :
                          corporation.syncStatus === 'in_progress' ? 'bg-yellow-500 animate-pulse' :
                          'bg-gray-600'
                        }`} title={`Sync: ${corporation.syncStatus}`} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="p-4 border-b border-gray-700 bg-gray-800">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-bold text-white">
                  {selectedNode?.type === 'summary'
                    ? 'All Buy Orders'
                    : selectedNode?.type === 'character'
                      ? selectedNode.character?.characterName
                      : selectedNode?.corporation?.corporationName || 'Buy Orders'}
                </h1>
                {selectedNode?.type === 'character' && selectedNode.character && (
                  <p className="text-sm text-gray-400 mt-1">
                    Last sync: {formatTimeAgo(selectedNode.character.lastSyncAt)}
                  </p>
                )}
                {selectedNode?.type === 'corporation' && selectedNode.corporation && (
                  <p className="text-sm text-gray-400 mt-1">
                    Last sync: {formatTimeAgo(selectedNode.corporation.lastSyncAt)}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3">
                {/* Filters */}
                <select
                  value={rangeFilter}
                  onChange={(e) => setRangeFilter(e.target.value)}
                  className="bg-gray-700 border border-gray-600 text-gray-300 text-sm rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Ranges</option>
                  {uniqueRanges.map(range => (
                    <option key={range} value={range}>{range}</option>
                  ))}
                </select>

                {/* Sync button */}
                {selectedNode?.type === 'character' && selectedNode.character && (
                  <button
                    onClick={() => handleSync(selectedNode.character!.characterId)}
                    disabled={syncing !== null}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    {syncing === selectedNode.character.characterId ? (
                      <>
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Syncing...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Sync Orders
                      </>
                    )}
                  </button>
                )}

                {/* Refresh All button */}
                {selectedNode?.type === 'summary' && (
                  <button
                    onClick={handleRefreshAll}
                    disabled={refreshingAll}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-500 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    {refreshingAll ? (
                      <>
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Syncing {refreshProgress}/{refreshTotal}...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Sync All Characters
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Summary Stats */}
          {selectedNode?.type === 'summary' && summary && (
            <div className="p-4 border-b border-gray-700 bg-gray-850">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-800 rounded-lg p-4">
                  <p className="text-2xl font-bold text-white">{summary.total}</p>
                  <p className="text-sm text-gray-400">Total Buy Orders</p>
                </div>
                <div className="bg-gray-800 rounded-lg p-4">
                  <p className="text-2xl font-bold text-yellow-400">{formatISK(summary.totalEscrow)} ISK</p>
                  <p className="text-sm text-gray-400">Total Escrow</p>
                </div>
                <div className="bg-gray-800 rounded-lg p-4">
                  <p className="text-2xl font-bold text-blue-400">{summary.byCharacter.length}</p>
                  <p className="text-sm text-gray-400">Characters with Orders</p>
                </div>
                <div className="bg-gray-800 rounded-lg p-4">
                  <p className="text-2xl font-bold text-green-400">
                    {filteredOrders.length > 0
                      ? Math.round(filteredOrders.reduce((sum, o) => sum + o.fillPercentage, 0) / filteredOrders.length)
                      : 0}%
                  </p>
                  <p className="text-sm text-gray-400">Avg Fill Rate</p>
                </div>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="m-4 p-4 bg-red-900/30 border border-red-700 rounded-lg">
              <p className="text-red-400">{error}</p>
            </div>
          )}

          {/* Orders Table */}
          <div className="flex-1 overflow-auto p-4">
            {ordersLoading ? (
              <div className="flex items-center justify-center h-full">
                <svg className="animate-spin h-8 w-8 text-blue-500" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            ) : filteredOrders.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <svg className="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <p className="text-lg">No buy orders found</p>
                <p className="text-sm mt-2">Sync your characters to load buy orders</p>
              </div>
            ) : (
              <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-900">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Item</th>
                      {selectedNode?.type === 'summary' && (
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Character</th>
                      )}
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">Price</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider">Volume</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider">Filled</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Range</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">Escrow</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Expires</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {filteredOrders.map(order => (
                      <tr key={order.id} className="hover:bg-gray-750">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <img
                              src={`https://images.evetech.net/types/${order.typeId}/icon?size=32`}
                              alt=""
                              className="w-6 h-6"
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            />
                            <span className="text-sm text-gray-300 truncate max-w-[200px]" title={order.typeName}>
                              {order.typeName}
                            </span>
                          </div>
                        </td>
                        {selectedNode?.type === 'summary' && (
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <img
                                src={`https://images.evetech.net/characters/${order.characterId}/portrait?size=32`}
                                alt={order.characterName}
                                className="w-6 h-6 rounded"
                              />
                              <span className="text-sm text-gray-300">{order.characterName}</span>
                            </div>
                          </td>
                        )}
                        <td className="px-4 py-3 text-right">
                          <span className="text-sm text-green-400 font-mono">
                            {order.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ISK
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-sm text-white font-mono">
                            {order.volumeRemain.toLocaleString()} / {order.volumeTotal.toLocaleString()}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <div className="w-16 h-2 bg-gray-700 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-blue-500 rounded-full"
                                style={{ width: `${order.fillPercentage}%` }}
                              />
                            </div>
                            <span className="text-xs text-gray-400">{order.fillPercentage}%</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex px-2 py-1 text-xs font-medium rounded bg-gray-700 text-gray-300">
                            {order.rangeName}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-sm text-yellow-400 font-mono">
                            {formatISK(order.escrow)} ISK
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-400">
                            {formatTimeRemaining(order.expiryDate)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default BuyOrders;
